import { FloatingFormItem, InputDate } from '@/components/_common';
import {
  usePortoScheduleAvailability,
  usePortoScheduleUpdate,
} from '@/core/domain/workorder/porto-schedule/porto-schedule.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import { DateTimeFormat } from '@/utils/moment';
import { Col, Form, Modal, Row, Skeleton, Typography } from 'antd';
import moment, { Moment } from 'moment';
import { useEffect, useMemo } from 'react';
import { useWorkorderDetailContext } from '../../../../WorkorderDetailProvider';
import ScheduleHourCard from '@/components/_common/ScheduleHourCard';

interface ComplementUpdateScheduleDateFormValues {
  scheduleCode: string;
  scheduleDate: Moment;
  capsCode: string;
  hour: string;
}

interface Props {
  opened: boolean;
  onClose: VoidFunction;
}

const { Title } = Typography;

export default function ComplementScheduleDateFieldEditModal(props: Props) {
  const ruleRequired = useDefaultEmptyFieldError();

  const portoScheduleUpdate = usePortoScheduleUpdate();

  const {
    complement: { data: complement },
    company: { data: companyData },
  } = useWorkorderDetailContext();

  const [form] = Form.useForm<ComplementUpdateScheduleDateFormValues>();

  const scheduleDate = Form.useWatch('scheduleDate', form);
  const hourWatch = Form.useWatch('hour', form);
  const capsCode = Form.useWatch('capsCode', form);

  useEffect(() => {
    if (props.opened) {
      form.setFieldsValue({
        scheduleCode: complement?.workOrderGuid,
        capsCode: companyData?.capsCode,
        scheduleDate: complement?.scheduleDateTime
          ? moment(complement.scheduleDateTime)
          : undefined,
      });
    }
  }, [complement, companyData, props.opened]);

  const portoScheduleAvailability = usePortoScheduleAvailability(
    {
      availabilityDate: scheduleDate?.format(DateTimeFormat),
      capsCodes: [capsCode],
    },
    !!capsCode && props.opened,
  );

  const hours = useMemo(
    () =>
      portoScheduleAvailability.data?.caps
        .at(0)
        ?.scheduleAvailability.find(
          (scheduleAvailabilityFind) =>
            scheduleAvailabilityFind.date === scheduleDate.format('DD-MM-YYYY'),
        )
        ?.timeSlots.map((time) => time.from),
    [portoScheduleAvailability.data, portoScheduleAvailability.dataUpdatedAt],
  );

  const handleCancel = () => {
    form.resetFields();
    props.onClose();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();

    await portoScheduleUpdate.mutateAsync({
      scheduleCode: values.scheduleCode,
      scheduleDate: values.scheduleDate.format(`YYYY-MM-DD[T]${values.hour}`),
    });

    handleCancel();
  };

  return (
    <Modal
      open={props.opened}
      onCancel={handleCancel}
      title="Alteração de data de agendamento"
      okText="Alterar"
      onOk={handleSubmit}
      confirmLoading={portoScheduleUpdate.isLoading}
      destroyOnClose
    >
      <Form form={form}>
        <Form.Item hidden name="capsCode" />
        <Form.Item hidden name="scheduleCode" />
        <Form.Item hidden name="hour" />

        <Row>
          <Col span={24}>
            <FloatingFormItem
              name="scheduleDate"
              label="Data de agendamento"
              rules={[ruleRequired]}
            >
              <InputDate showTime />
            </FloatingFormItem>
          </Col>

          <Skeleton active loading={portoScheduleAvailability.isLoading}>
            {!!hours?.length && (
              <Col span={24}>
                <div style={{ marginBottom: 18 }}>
                  <Title level={4}>Horários disponíveis</Title>
                </div>

                <Row gutter={[12, 12]}>
                  {hours.map((hour) => (
                    <Col span={24} md={6}>
                      <ScheduleHourCard
                        active={hourWatch === hour}
                        value={hour}
                        onClick={() => form.setFieldValue('hour', hour)}
                      />
                    </Col>
                  ))}
                </Row>
              </Col>
            )}
          </Skeleton>
        </Row>
      </Form>
    </Modal>
  );
}
